import type { ReactNode } from 'react';
import type {
  SessionStatus,
  SubscriptionStatus,
  TrialStage,
  TutorApplicationStatus,
  UserRole,
} from '../types/api';
import { SESSION_STATUS_LABEL } from '../lib/format';

type Tone = 'neutral' | 'accent' | 'live' | 'good' | 'danger';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2',
  accent: 'bg-accent-wash text-accent',
  live: 'bg-live-wash text-live-text',
  good: 'bg-good-wash text-good',
  danger: 'bg-danger-wash text-danger',
};

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  return <span className={`badge ${TONES[tone]}`}>{children}</span>;
}

/**
 * Status always ships as colour *plus* its name — a session that is live is
 * the single most consequential state on any screen here, and it must not
 * depend on a viewer distinguishing amber from green.
 */
const SESSION_TONES: Record<SessionStatus, Tone> = {
  scheduled: 'accent',
  in_progress: 'live',
  completed: 'good',
  cancelled: 'danger',
  no_show: 'neutral',
};

export function SessionStatusBadge({ status }: { status: SessionStatus }) {
  return (
    <Badge tone={SESSION_TONES[status]}>
      {status === 'in_progress' && <span className="pulse-dot" aria-hidden="true" />}
      {SESSION_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

/**
 * The funnel's stages, in order. Colour carries the same meaning as
 * elsewhere here — amber is "someone has to act now" — and the label is
 * always present, so the queue never depends on telling two hues apart.
 */
const TRIAL_TONES: Record<TrialStage, Tone> = {
  pending_payment: 'neutral',
  awaiting_assignment: 'live',
  assigned: 'accent',
  completed: 'live',
  converted: 'good',
  cancelled: 'danger',
  expired: 'neutral',
};

export const TRIAL_STAGE_LABEL: Record<TrialStage, string> = {
  pending_payment: 'Awaiting payment',
  awaiting_assignment: 'Needs a tutor',
  assigned: 'Tutor assigned',
  completed: 'Pending subscription',
  converted: 'Subscribed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

export function TrialStageBadge({ stage }: { stage: TrialStage }) {
  return (
    <Badge tone={TRIAL_TONES[stage]}>
      {(stage === 'awaiting_assignment' || stage === 'completed') && (
        <span className="pulse-dot" aria-hidden="true" />
      )}
      {TRIAL_STAGE_LABEL[stage] ?? stage}
    </Badge>
  );
}

export const APPLICATION_STATUS_LABEL: Record<TutorApplicationStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  rejected: 'Not approved',
  suspended: 'Suspended',
};

/**
 * `submitted` carries the pulse for the same reason a trial waiting for a
 * tutor does: it is a queue somebody is sitting in.
 */
const APPLICATION_TONES: Record<TutorApplicationStatus, Tone> = {
  draft: 'neutral',
  submitted: 'accent',
  changes_requested: 'live',
  approved: 'good',
  rejected: 'danger',
  suspended: 'danger',
};

export function ApplicationStatusBadge({ status }: { status: TutorApplicationStatus }) {
  return (
    <Badge tone={APPLICATION_TONES[status]}>
      {status === 'submitted' && <span className="pulse-dot" aria-hidden="true" />}
      {APPLICATION_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

const SUBSCRIPTION_TONES: Record<SubscriptionStatus, Tone> = {
  active: 'good',
  paused: 'live',
  completed: 'neutral',
  cancelled: 'danger',
};

export function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  return <Badge tone={SUBSCRIPTION_TONES[status]}>{status}</Badge>;
}

const ROLE_TONES: Record<UserRole, Tone> = {
  admin: 'accent',
  tutor: 'neutral',
  student: 'neutral',
};

export function RoleBadge({ role }: { role: UserRole }) {
  return <Badge tone={ROLE_TONES[role]}>{role}</Badge>;
}

export function ActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge tone={isActive ? 'good' : 'danger'}>{isActive ? 'Active' : 'Suspended'}</Badge>
  );
}

/** Online/offline. The dot alone is never the whole message — it has a label. */
export function PresenceDot({ online, label }: { online: boolean; label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-3">
      {online ? (
        <span className="pulse-dot" aria-hidden="true" />
      ) : (
        <span
          className="inline-block size-2 rounded-full bg-rule-strong"
          aria-hidden="true"
        />
      )}
      {label ?? (online ? 'Online' : 'Offline')}
    </span>
  );
}
